import React, { useState, useEffect } from "react";

/**
 * Step 3: Field Mapping
 * Parse CSV files and map columns to database fields
 */
function Step3FieldMapping({
  migrationData,
  updateMigrationData,
  nextStep,
  prevStep,
  type = "business",
}) {
  const [csvHeaders, setCsvHeaders] = useState({
    services: [],
    customers: [],
    appointments: [],
    staff: [],
  });
  const [mappings, setMappings] = useState({
    services: {},
    customers: {},
    appointments: {},
    staff: {},
  });
  const [loading, setLoading] = useState(true);

  // Database field definitions
  // For freelancers, exclude "Staff Member" from appointments - they are the only staff
  const getFieldDefinitions = () => {
    const baseDefinitions = {
      services: [
        { key: "name", label: "Service Name", required: true },
        { key: "category", label: "Category", required: false },
        { key: "price", label: "Price", required: true },
        { key: "duration", label: "Duration (minutes)", required: true },
        { key: "description", label: "Description", required: false },
        { key: "sku", label: "SKU/Code", required: false },
      ],
      customers: [
        { key: "firstName", label: "First Name", required: true },
        { key: "lastName", label: "Last Name", required: true },
        { key: "email", label: "Email", required: true },
        { key: "phone", label: "Phone", required: false },
        { key: "birthday", label: "Birthday", required: false },
        { key: "notes", label: "Notes", required: false },
      ],
      appointments: [
        { key: "date", label: "Date", required: true },
        { key: "time", label: "Time", required: true },
        { key: "customerEmail", label: "Customer Email", required: true },
        { key: "serviceName", label: "Service Name", required: true },
        // Only include Staff Member for business imports, not freelancers
        ...(type === "business"
          ? [{ key: "staffName", label: "Staff Member", required: false }]
          : []),
        { key: "duration", label: "Duration (minutes)", required: false },
        { key: "price", label: "Price", required: false },
        { key: "status", label: "Status", required: false },
        { key: "notes", label: "Notes", required: false },
      ],
      staff: [
        { key: "firstName", label: "First Name", required: true },
        { key: "lastName", label: "Last Name", required: true },
        { key: "email", label: "Email", required: true },
        { key: "phone", label: "Phone", required: false },
        { key: "role", label: "Role/Title", required: false },
        { key: "specialties", label: "Specialties", required: false },
      ],
    };
    return baseDefinitions;
  };

  const fieldDefinitions = getFieldDefinitions();

  useEffect(() => {
    parseCSVFiles();
  }, []);

  const parseCSVFiles = async () => {
    setLoading(true);
    const newHeaders = {};
    const newMappings = {};

    for (const [fileType, file] of Object.entries(
      migrationData.uploadedFiles,
    )) {
      if (!file) continue;

      try {
        const text = await file.text();
        // Handle both Windows (\r\n) and Unix (\n) line endings
        const lines = text.split(/\r?\n/);
        if (lines.length > 0) {
          // Parse headers - handle quoted values and trim whitespace
          const headers = lines[0].split(",").map((h) =>
            h
              .trim()
              .replace(/^["']|["']$/g, "")
              .trim(),
          );
          newHeaders[fileType] = headers;

          // Auto-detect mappings
          const autoMappings = {};
          fieldDefinitions[fileType]?.forEach((field) => {
            const match = headers.find(
              (h) =>
                h.toLowerCase().includes(field.key.toLowerCase()) ||
                field.label.toLowerCase().includes(h.toLowerCase()),
            );
            if (match) {
              autoMappings[field.key] = match;
            }
          });
          newMappings[fileType] = autoMappings;
        }
      } catch (err) {
      }
    }

    setCsvHeaders(newHeaders);
    setMappings(newMappings);
    setLoading(false);
  };

  const handleMappingChange = (fileType, dbField, csvColumn) => {
    setMappings({
      ...mappings,
      [fileType]: {
        ...mappings[fileType],
        [dbField]: csvColumn,
      },
    });
  };

  const handleContinue = () => {
    // Validate required fields are mapped
    let hasErrors = false;
    const fileTypesToCheck = Object.keys(migrationData.uploadedFiles).filter(
      (type) => migrationData.uploadedFiles[type],
    );

    for (const fileType of fileTypesToCheck) {
      const requiredFields =
        fieldDefinitions[fileType]?.filter((f) => f.required) || [];
      for (const field of requiredFields) {
        if (!mappings[fileType]?.[field.key]) {
          alert(
            `Please map the required field "${field.label}" for ${fileType}`,
          );
          hasErrors = true;
          break;
        }
      }
      if (hasErrors) break;
    }

    if (!hasErrors) {
      updateMigrationData({ fieldMappings: mappings });
      nextStep();
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Analyzing your CSV files...</p>
        </div>
      </div>
    );
  }

  const uploadedFileTypes = Object.keys(migrationData.uploadedFiles).filter(
    (type) => migrationData.uploadedFiles[type],
  );

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Step 3: Field Mapping</h2>
      <p style={styles.subtitle}>
        We've auto-detected most field mappings. Please review and adjust if
        needed.
      </p>

      {uploadedFileTypes.map((fileType) => (
        <div key={fileType} style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {fileType.charAt(0).toUpperCase() + fileType.slice(1)}
          </h3>
          <div style={styles.mappingTable}>
            <div style={styles.tableHeader}>
              <div style={styles.headerCell}>Ogestyle Field</div>
              <div style={styles.headerCell}>Your CSV Column</div>
            </div>
            {fieldDefinitions[fileType]?.map((field) => (
              <div key={field.key} style={styles.tableRow}>
                <div style={styles.labelCell}>
                  {field.label}
                  {field.required && <span style={styles.required}> *</span>}
                </div>
                <div style={styles.selectCell}>
                  <select
                    value={mappings[fileType]?.[field.key] || ""}
                    onChange={(e) =>
                      handleMappingChange(fileType, field.key, e.target.value)
                    }
                    style={styles.select}
                  >
                    <option value="">-- Select Column --</option>
                    {csvHeaders[fileType]?.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={styles.actions}>
        <button onClick={prevStep} style={styles.backButton}>
          ← Back
        </button>
        <button onClick={handleContinue} style={styles.continueButton}>
          Continue →
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#111",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "32px",
  },
  loading: {
    textAlign: "center",
    padding: "48px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #2196F3",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },
  section: {
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "16px",
    color: "#333",
    textTransform: "capitalize",
  },
  mappingTable: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    overflow: "hidden",
  },
  tableHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    backgroundColor: "#f5f5f5",
    padding: "12px 16px",
    fontWeight: "600",
    fontSize: "14px",
    color: "#333",
  },
  headerCell: {
    padding: "0 8px",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    padding: "12px 16px",
    borderTop: "1px solid #e0e0e0",
    alignItems: "center",
  },
  labelCell: {
    fontSize: "14px",
    color: "#333",
    padding: "0 8px",
  },
  required: {
    color: "#f44336",
    fontWeight: "700",
  },
  selectCell: {
    padding: "0 8px",
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  backButton: {
    padding: "12px 24px",
    backgroundColor: "#fff",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButton: {
    padding: "12px 32px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default Step3FieldMapping;
